import { sql } from "drizzle-orm";
import { pgTable, varchar, integer, boolean, jsonb, timestamp, date, uniqueIndex, index } from "drizzle-orm/pg-core";
import type { AlertSettings, AlertPromo } from "./account-performance-alerts";

// Deliberately retain audit evidence when an enrollment is disabled or remapped.
export const accountAlertConfig = pgTable("account_alert_config", {
  id: varchar("id").primaryKey().default("global"),
  settings: jsonb("settings").$type<AlertSettings>().notNull(),
  calculatorVersion: varchar("calculator_version").notNull(),
  approvedHash: varchar("approved_hash"),
  campaignApprovedHash: varchar("campaign_approved_hash"),
  approvedBy: varchar("approved_by"),
  approvedAt: timestamp("approved_at"),
  deliveryEnabled: boolean("delivery_enabled").notNull().default(false),
  schedulerEnabled: boolean("scheduler_enabled").notNull().default(false),
  collectionEnabled: boolean("collection_enabled").notNull().default(false),
  inputSource: varchar("input_source").notNull().default("analytics"),
  sourceEpoch: varchar("source_epoch").notNull().default("legacy"),
  analyticsDiscovery: jsonb("analytics_discovery").$type<Record<string, any>>(),
  applicationUrl: varchar("application_url"),
  revision: integer("revision").notNull().default(1),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const accountAlertEnrollments = pgTable("account_alert_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull(),
  accountId: varchar("account_id").notNull(),
  referenceKey: varchar("reference_key"),
  event: varchar("event").notNull(),
  enabled: boolean("enabled").notNull().default(false),
  overrides: jsonb("overrides").$type<Partial<AlertSettings>>().notNull().default(sql`'{}'::jsonb`),
  promotions: jsonb("promotions").$type<AlertPromo[]>().notNull().default(sql`'[]'::jsonb`),
  revision: integer("revision").notNull().default(1),
  updatedBy: varchar("updated_by").notNull(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [uniqueIndex("account_alert_enrollment_account_uq").on(t.accountId)]);
export const accountAlertEvaluations = pgTable("account_alert_evaluations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  enrollmentId: varchar("enrollment_id").notNull(),
  identity: varchar("identity").notNull(),
  day: date("day").notNull(),
  snapshot: jsonb("snapshot").$type<Record<string, any>>().notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, t => [uniqueIndex("account_alert_evaluation_identity_uq").on(t.identity), index("account_alert_evaluation_history_idx").on(t.enrollmentId, t.createdAt)]);
export const accountAlertProblems = pgTable("account_alert_problems", {
  enrollmentId: varchar("enrollment_id").primaryKey(),
  epoch: integer("epoch").notNull().default(0),
  active: boolean("active").notNull().default(false),
  lastDay: date("last_day"),
  configuration: varchar("configuration"),
  evaluationId: varchar("evaluation_id"),
  openedAt: timestamp("opened_at"),
  recoveredAt: timestamp("recovered_at"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const accountAlertJobs = pgTable("account_alert_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  enrollmentId: varchar("enrollment_id").notNull(),
  day: date("day").notNull(),
  configuration: varchar("configuration").notNull(),
  pendingDays: jsonb("pending_days").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  state: varchar("state").notNull().default("queued"),
  token: varchar("token"),
  leaseUntil: timestamp("lease_until"),
  retryAt: timestamp("retry_at").notNull().defaultNow(),
  lastAttemptAt: timestamp("last_attempt_at"),
  reason: varchar("reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, t => [uniqueIndex("account_alert_job_identity_uq").on(t.enrollmentId, t.day, t.configuration)]);
export const accountAlertDeliveries = pgTable("account_alert_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key").notNull(),
  enrollmentId: varchar("enrollment_id"),
  evaluationId: varchar("evaluation_id"),
  configuration: varchar("configuration").notNull(),
  kind: varchar("kind").notNull(),
  state: varchar("state").notNull().default("pending"),
  payload: jsonb("payload").$type<Record<string, any>>().notNull(),
  token: varchar("token"),
  attemptedAt: timestamp("attempted_at"),
  settledAt: timestamp("settled_at"),
  reason: varchar("reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, t => [uniqueIndex("account_alert_delivery_key_uq").on(t.key)]);
export const accountAlertAudit = pgTable("account_alert_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  actor: varchar("actor").notNull(),
  action: varchar("action").notNull(),
  snapshot: jsonb("snapshot").$type<Record<string, any>>().notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const accountAlertDayEvidence = pgTable("account_alert_day_evidence", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull(),
  accountId: varchar("account_id").notNull(),
  day: date("day").notNull(),
  event: varchar("event").notNull(),
  runId: varchar("run_id").notNull(),
  certificate: jsonb("certificate").$type<Record<string, any>>().notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, t => [index("account_alert_evidence_scope_idx").on(t.brandId, t.accountId, t.day)]);

export const accountAlertRuns = pgTable("account_alert_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  identity: varchar("identity").notNull(),
  day: date("day").notNull(),
  revision: integer("revision").notNull(),
  members: jsonb("members").$type<Array<{ enrollmentId: string; configuration: string }>>().notNull(),
  state: varchar("state").notNull().default("queued"),
  token: varchar("token"),
  leaseUntil: timestamp("lease_until"),
  retryAt: timestamp("retry_at").notNull().defaultNow(),
  heartbeatAt: timestamp("heartbeat_at"),
  passes: integer("passes").notNull().default(0),
  stalls: integer("stalls").notNull().default(0),
  progress: jsonb("progress").$type<Record<string, any>>().notNull().default(sql`'{}'::jsonb`),
  reason: varchar("reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  finishedAt: timestamp("finished_at"),
}, t => [
  uniqueIndex("account_alert_run_identity_uq").on(t.identity),
  uniqueIndex("account_alert_run_active_uq").on(sql`(true)`).where(sql`${t.state} in ('queued','running')`),
  index("account_alert_run_recent_idx").on(t.createdAt),
]);

export const accountAlertWorker = pgTable("account_alert_worker", {
  id: varchar("id").primaryKey(),
  heartbeatAt: timestamp("heartbeat_at").notNull().defaultNow(),
});

// Separate from account-window jobs/calibration; a partial population never replaces last-good.
export const campaignAlertEnrollments = pgTable("campaign_alert_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brandId: varchar("brand_id").notNull(),
  accountId: varchar("account_id").notNull(),
  event: varchar("event"),
  enabled: boolean("enabled").notNull().default(false),
  reason: varchar("reason").notNull().default("reconciliation_pending"),
  revision: integer("revision").notNull().default(1),
  // No inherited global approval can authorize a newly introduced identity.
  approvedConfiguration: varchar("approved_configuration"),
  updatedBy: varchar("updated_by").notNull().default("launcher-reconciliation"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [uniqueIndex("campaign_alert_enrollment_account_uq").on(t.accountId)]);
export const campaignAlertEnrollmentChanges = pgTable("campaign_alert_enrollment_changes", {
  accountId: varchar("account_id").primaryKey(),
  lifecycleChanged: boolean("lifecycle_changed").notNull().default(true),
});
export const campaignAlertCollections = pgTable("campaign_alert_collections", {
  enrollmentId: varchar("enrollment_id").primaryKey(),
  configuration: varchar("configuration").notNull(),
  state: varchar("state").notNull(),
  pages: jsonb("pages").$type<import("../server/services/campaign-lifetime-evidence").CampaignLifetimePage[]>().notNull().default(sql`'[]'::jsonb`),
  snapshot: jsonb("snapshot").$type<import("../server/services/campaign-lifetime-evidence").CampaignLifetimeSnapshot>(),
  reason: varchar("reason"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const campaignAlertEvaluations = pgTable("campaign_alert_evaluations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  enrollmentId: varchar("enrollment_id").notNull(),
  identity: varchar("identity").notNull(),
  snapshot: jsonb("snapshot").$type<Record<string, any>>().notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, t => [uniqueIndex("campaign_alert_evaluation_identity_uq").on(t.identity)]);
export const campaignAlertIncidents = pgTable("campaign_alert_incidents", {
  identity: varchar("identity").primaryKey(),
  enrollmentId: varchar("enrollment_id").notNull(),
  campaignId: varchar("campaign_id").notNull(),
  active: boolean("active").notNull().default(false),
  epoch: integer("epoch").notNull().default(0),
  sourceRevision: integer("source_revision").notNull().default(0),
  evaluationId: varchar("evaluation_id"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
